// Recetas (BOM) por producto padre, factores de consumo por talla y tarifas MO/CIF.
// Producción → Fabricación → Recetas y costos.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Producto, Switch, fieldCls, inv, num, toNum } from '@/features/inventario/shared';
import { Bom, BomLinea, ConfigCosto, TallaFactor, invalidateProduccion, prod } from './shared';

type Tab = 'bom' | 'tallas' | 'tarifas';

interface MaterialOpt { id: string; codigo: string; nombre: string; unidad_medida: string | null }
interface TallaValor { id: string; valor: string; orden: number }
interface CategoriaOpt { id: string; codigo: string | null; nombre: string }

export function RecetasProduccion() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('bom');
  const [padreId, setPadreId] = useState('');

  // ---- Catálogos base ----
  const { data: padres = [] } = useQuery<Producto[]>({
    queryKey: ['prod-padres'],
    queryFn: () => inv.list<Producto>('productos', '?select=id,codigo,nombre,categoria_id&tiene_variantes=eq.true&deleted_at=is.null&order=codigo'),
  });
  const { data: materiales = [] } = useQuery<MaterialOpt[]>({
    queryKey: ['prod-materiales'],
    queryFn: () => inv.list<MaterialOpt>('productos', '?select=id,codigo,nombre,unidad_medida&tiene_variantes=eq.false&deleted_at=is.null&order=nombre'),
  });
  const { data: categorias = [] } = useQuery<CategoriaOpt[]>({
    queryKey: ['prod-categorias'],
    queryFn: () => inv.list<CategoriaOpt>('categorias', '?select=id,codigo,nombre&deleted_at=is.null&order=nombre'),
  });

  const invalidar = () => invalidateProduccion(qc);

  // ============================ TAB: RECETA (BOM) ============================
  const { data: boms = [] } = useQuery<Bom[]>({
    queryKey: ['prod-bom', padreId], enabled: !!padreId,
    queryFn: () => prod.list<Bom>('bom', `?select=*&producto_id=eq.${padreId}&deleted_at=is.null`),
  });
  const bom = boms[0];
  const { data: lineas = [] } = useQuery<BomLinea[]>({
    queryKey: ['prod-bom-lineas', bom?.id], enabled: !!bom?.id,
    queryFn: () => prod.list<BomLinea>('bom_lineas', `?select=*&bom_id=eq.${bom!.id}&deleted_at=is.null&order=created_at`),
  });

  const crearBom = useMutation({
    mutationFn: () => prod.create('bom', { producto_id: padreId, nombre: 'Receta ' + (padres.find((p) => p.id === padreId)?.nombre ?? '') }),
    onSuccess: () => { toast.success('Receta creada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [lnMaterial, setLnMaterial] = useState('');
  const [lnCantidad, setLnCantidad] = useState('');
  const [lnEscala, setLnEscala] = useState(false);
  const [lnNotas, setLnNotas] = useState('');

  const agregarLinea = useMutation({
    mutationFn: () => prod.create('bom_lineas', {
      bom_id: bom!.id, material_id: lnMaterial, cantidad: toNum(lnCantidad), escala_talla: lnEscala, notas: lnNotas || null,
    }),
    onSuccess: () => { toast.success('Material agregado'); setLnMaterial(''); setLnCantidad(''); setLnEscala(false); setLnNotas(''); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitarLinea = useMutation({
    mutationFn: (id: string) => prod.remove('bom_lineas', id),
    onSuccess: () => { toast.success('Material quitado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const matInfo = (id: string) => materiales.find((m) => m.id === id);

  // ============================ TAB: FACTORES TALLA ============================
  const { data: tallas = [] } = useQuery<TallaValor[]>({
    queryKey: ['prod-tallas'],
    queryFn: async () => {
      const attrs = await inv.list<{ id: string }>('atributos', '?select=id&codigo=eq.TALLA');
      if (!attrs.length) return [];
      return inv.list<TallaValor>('atributo_valores', `?select=id,valor,orden&atributo_id=eq.${attrs[0].id}&es_activo=eq.true&order=orden`);
    },
  });
  const { data: factores = [] } = useQuery<TallaFactor[]>({
    queryKey: ['prod-talla-factores'],
    queryFn: () => prod.list<TallaFactor>('talla_factores', '?select=id,valor_id,factor&deleted_at=is.null'),
  });
  const [factorEdit, setFactorEdit] = useState<Record<string, string>>({});

  const guardarFactor = useMutation({
    mutationFn: async ({ valorId, factor }: { valorId: string; factor: number }) => {
      const existente = factores.find((f) => f.valor_id === valorId);
      if (existente) return prod.update('talla_factores', existente.id, { factor });
      return prod.create('talla_factores', { valor_id: valorId, factor });
    },
    onSuccess: () => { toast.success('Factor guardado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // ============================ TAB: TARIFAS MO/CIF ============================
  const { data: tarifas = [] } = useQuery<ConfigCosto[]>({
    queryKey: ['prod-tarifas'],
    queryFn: () => prod.list<ConfigCosto>('config_costos', '?select=*&deleted_at=is.null&order=created_at'),
  });
  const [tfDestino, setTfDestino] = useState<'categoria' | 'producto'>('categoria');
  const [tfCategoria, setTfCategoria] = useState('');
  const [tfProducto, setTfProducto] = useState('');
  const [tfMo, setTfMo] = useState('');
  const [tfCif, setTfCif] = useState('');

  const crearTarifa = useMutation({
    mutationFn: () => prod.create('config_costos', {
      categoria_id: tfDestino === 'categoria' ? tfCategoria : null,
      producto_id: tfDestino === 'producto' ? tfProducto : null,
      mo_unitario: toNum(tfMo), cif_unitario: toNum(tfCif),
    }),
    onSuccess: () => { toast.success('Tarifa guardada'); setTfCategoria(''); setTfProducto(''); setTfMo(''); setTfCif(''); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitarTarifa = useMutation({
    mutationFn: (id: string) => prod.remove('config_costos', id),
    onSuccess: () => { toast.success('Tarifa eliminada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const catName = (id: string | null) => categorias.find((c) => c.id === id)?.nombre ?? null;
  const padreName = (id: string | null) => padres.find((p) => p.id === id)?.nombre ?? null;

  const tabs: { key: Tab; label: string }[] = [
    { key: 'bom', label: 'Recetas (BOM)' },
    { key: 'tallas', label: 'Factores por talla' },
    { key: 'tarifas', label: 'Tarifas MO/CIF' },
  ];

  const lineasOrdenadas = useMemo(() => lineas, [lineas]);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 border-b">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'bom' && (
        <Card>
          <CardHeader><CardTitle>Receta por producto (consumo por unidad)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Campo label="Producto padre (modelo)" className="max-w-xl">
              <select className={fieldCls} value={padreId} onChange={(e) => setPadreId(e.target.value)}>
                <option value="">— Selecciona un modelo —</option>
                {padres.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
              </select>
            </Campo>

            {padreId && !bom && (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground flex items-center gap-3">
                Este modelo aún no tiene receta.
                <Button size="sm" onClick={() => crearBom.mutate()} isLoading={crearBom.isPending}>
                  <Plus className="h-4 w-4 mr-1" /> Crear receta
                </Button>
              </div>
            )}

            {bom && (
              <>
                <div className="grid gap-2 md:grid-cols-[2fr_1fr_auto_2fr_auto] items-end">
                  <Campo label="Material (MP)">
                    <select className={fieldCls} value={lnMaterial} onChange={(e) => setLnMaterial(e.target.value)}>
                      <option value="">— Material —</option>
                      {materiales.map((m) => <option key={m.id} value={m.id}>{m.codigo} · {m.nombre}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Cantidad / unidad">
                    <input type="number" min="0" step="0.0001" className={fieldCls} value={lnCantidad}
                      onChange={(e) => setLnCantidad(e.target.value)} />
                  </Campo>
                  <div className="pb-2"><Switch checked={lnEscala} onChange={setLnEscala} label="Escala por talla" /></div>
                  <Campo label="Notas">
                    <input className={fieldCls} value={lnNotas} onChange={(e) => setLnNotas(e.target.value)} />
                  </Campo>
                  <Button className="mb-0.5" disabled={!lnMaterial || toNum(lnCantidad) <= 0}
                    onClick={() => agregarLinea.mutate()} isLoading={agregarLinea.isPending}>
                    <Plus className="h-4 w-4 mr-1" /> Agregar
                  </Button>
                </div>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="py-2 pr-3 text-right">N°</th>
                      <th className="py-2 pr-3 text-left">Material</th>
                      <th className="py-2 pr-3 text-left">Unidad</th>
                      <th className="py-2 pr-3 text-right">Cantidad</th>
                      <th className="py-2 pr-3 text-center">Escala talla</th>
                      <th className="py-2 pr-3 text-left">Notas</th>
                      <th className="py-2 pr-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {lineasOrdenadas.map((l, i) => {
                      const m = matInfo(l.material_id);
                      return (
                        <tr key={l.id} className="border-b last:border-0">
                          <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                          <td className="py-2 pr-3">{m ? `${m.codigo} · ${m.nombre}` : l.material_id}</td>
                          <td className="py-2 pr-3">{m?.unidad_medida ?? '—'}</td>
                          <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(l.cantidad, 4)}</td>
                          <td className="py-2 pr-3 text-center">{l.escala_talla ? 'Sí' : '—'}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{l.notas ?? ''}</td>
                          <td className="py-2 pr-3 text-right">
                            <Button variant="ghost" size="sm" onClick={() => quitarLinea.mutate(l.id)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {!lineas.length && (
                      <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Sin materiales en la receta</td></tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'tallas' && (
        <Card>
          <CardHeader><CardTitle>Factor de consumo por talla (solo materiales con “escala por talla”)</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full max-w-xl text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="py-2 pr-3 text-right">N°</th>
                  <th className="py-2 pr-3 text-left">Talla</th>
                  <th className="py-2 pr-3 text-right">Factor</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {tallas.map((t, i) => {
                  const f = factores.find((x) => x.valor_id === t.id);
                  const val = factorEdit[t.id] ?? (f ? String(f.factor) : '1');
                  return (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3 font-medium">{t.valor}</td>
                      <td className="py-2 pr-3 text-right">
                        <input type="number" min="0.1" step="0.01" className={`${fieldCls} w-28 text-right ml-auto`} value={val}
                          onChange={(e) => setFactorEdit((s) => ({ ...s, [t.id]: e.target.value }))} />
                      </td>
                      <td className="py-2 pr-3">
                        <Button size="sm" variant="outline" isLoading={guardarFactor.isPending}
                          onClick={() => guardarFactor.mutate({ valorId: t.id, factor: toNum(val) || 1 })}>
                          Guardar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!tallas.length && (
                  <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">No hay atributo TALLA configurado</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === 'tarifas' && (
        <Card>
          <CardHeader><CardTitle>Tarifas estándar de conversión (MO + CIF por prenda)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 md:grid-cols-[auto_2fr_1fr_1fr_auto] items-end">
              <Campo label="Aplicar a">
                <select className={fieldCls} value={tfDestino} onChange={(e) => setTfDestino(e.target.value as 'categoria' | 'producto')}>
                  <option value="categoria">Categoría</option>
                  <option value="producto">Producto (modelo)</option>
                </select>
              </Campo>
              {tfDestino === 'categoria' ? (
                <Campo label="Categoría">
                  <select className={fieldCls} value={tfCategoria} onChange={(e) => setTfCategoria(e.target.value)}>
                    <option value="">— Categoría —</option>
                    {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </Campo>
              ) : (
                <Campo label="Producto padre">
                  <select className={fieldCls} value={tfProducto} onChange={(e) => setTfProducto(e.target.value)}>
                    <option value="">— Modelo —</option>
                    {padres.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                  </select>
                </Campo>
              )}
              <Campo label="MO por unidad (USD)">
                <input type="number" min="0" step="0.01" className={fieldCls} value={tfMo} onChange={(e) => setTfMo(e.target.value)} />
              </Campo>
              <Campo label="CIF por unidad (USD)">
                <input type="number" min="0" step="0.01" className={fieldCls} value={tfCif} onChange={(e) => setTfCif(e.target.value)} />
              </Campo>
              <Button className="mb-0.5" isLoading={crearTarifa.isPending}
                disabled={tfDestino === 'categoria' ? !tfCategoria : !tfProducto}
                onClick={() => crearTarifa.mutate()}>
                <Plus className="h-4 w-4 mr-1" /> Guardar
              </Button>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="py-2 pr-3 text-right">N°</th>
                  <th className="py-2 pr-3 text-left">Aplica a</th>
                  <th className="py-2 pr-3 text-right">MO</th>
                  <th className="py-2 pr-3 text-right">CIF</th>
                  <th className="py-2 pr-3 text-right">Total conversión</th>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {tarifas.map((t, i) => (
                  <tr key={t.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">
                      {t.producto_id
                        ? <>Modelo: <span className="font-medium">{padreName(t.producto_id) ?? t.producto_id}</span></>
                        : <>Categoría: <span className="font-medium">{catName(t.categoria_id) ?? t.categoria_id}</span></>}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(t.mo_unitario)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(t.cif_unitario)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{num(Number(t.mo_unitario) + Number(t.cif_unitario))}</td>
                    <td className="py-2 pr-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => quitarTarifa.mutate(t.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {!tarifas.length && (
                  <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">
                    Sin tarifas — sin tarifa la OT se costea solo con materia prima
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(agregarLinea.isPending || crearTarifa.isPending) && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Guardando…</div>
      )}
    </div>
  );
}
